import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useGLTF } from '@react-three/drei'
import * as THREE from 'three'
import { Base, Geometry, Subtraction } from '@react-three/csg'
import WallShaderMaterial from '../../materials/WallShaderMaterial'
import { extend } from '@react-three/fiber'
import { cloneAndScaleUV, setupTextureRepeat } from '../../utils/geometryUltis'
import applyNormalDirectionFilter from '../../utils/normalDirectionFilter'

extend({ WallShaderMaterial })

// ======================================================
// ✅ PARAMS
// ======================================================
const Y_TOP_TOL = 0.12
const Y_SAMPLE_EPS_XZ = 0.01

// UWAGA: dla stromych dachów filtr "outerDir dot normal" potrafił wycinać całą połać,
// bo outerDir był praktycznie (0,1,0). Naprawa: outerDir filtrujemy tylko w XZ (projekcja),
// a jeśli outerDir jest prawie pionowy, filtr ignorujemy.
const ROOF_OUT_MIN_DOT = 0.15
const ROOF_CLIP_MARGIN_XZ = 0.8

function ensureBoundingBox(geo) {
  if (geo && !geo.boundingBox) geo.computeBoundingBox()
  return geo
}

function getCenterFromGeo(geo) {
  ensureBoundingBox(geo)
  const c = new THREE.Vector3()
  geo.boundingBox?.getCenter(c)
  return c
}

function unionBoxes(geos) {
  const box = new THREE.Box3()
  geos.forEach((g) => {
    ensureBoundingBox(g)
    if (g?.boundingBox) box.union(g.boundingBox)
  })
  return box
}

// ======================================================
// ✅ Raycast sampler: yMax(x,z) (najwyższy hit)
// ======================================================
function makeRoofYMaxSampler(roofMesh, epsXZ = Y_SAMPLE_EPS_XZ) {
  const roofWorldBox = new THREE.Box3().setFromObject(roofMesh)
  const originY = roofWorldBox.max.y + 5
  const raycaster = new THREE.Raycaster()
  const down = new THREE.Vector3(0, -1, 0)

  const keyXZ = (x, z) => `${Math.round(x / epsXZ)},${Math.round(z / epsXZ)}`
  const cache = new Map()

  return (x, z) => {
    const k = keyXZ(x, z)
    const cached = cache.get(k)
    if (cached != null) return cached

    raycaster.set(new THREE.Vector3(x, originY, z), down)
    const hits = raycaster.intersectObject(roofMesh, false)

    let best = -Infinity
    if (hits && hits.length) {
      for (let i = 0; i < hits.length; i++) best = Math.max(best, hits[i].point.y)
    } else {
      best = roofWorldBox.max.y
    }

    cache.set(k, best)
    return best
  }
}

// ======================================================
// ✅ LOOP helpers (segments -> ordered loop)
// ======================================================
function vKey(p, eps = 1e-3) {
  return `${Math.round(p.x / eps)},${Math.round(p.y / eps)},${Math.round(p.z / eps)}`
}

function buildLoopFromSegments(segments, eps = 1e-3) {
  if (!segments?.length) return []

  const verts = []
  const idxByKey = new Map()

  const getIdx = (p) => {
    const k = vKey(p, eps)
    let idx = idxByKey.get(k)
    if (idx == null) {
      idx = verts.length
      verts.push(p.clone())
      idxByKey.set(k, idx)
    }
    return idx
  }

  const adj = new Map()
  const addAdj = (i, j) => {
    if (!adj.has(i)) adj.set(i, [])
    adj.get(i).push(j)
  }

  for (const s of segments) {
    const ia = getIdx(s.a)
    const ib = getIdx(s.b)
    addAdj(ia, ib)
    addAdj(ib, ia)
  }

  let start = 0
  for (const [i, list] of adj.entries()) {
    if (list.length === 1) {
      start = i
      break
    }
  }

  const visitedEdge = new Set()
  const edgeKey = (i, j) => (i < j ? `${i}|${j}` : `${j}|${i}`)

  const path = []
  let current = start
  let prev = null

  while (true) {
    path.push(verts[current].clone())

    const neigh = adj.get(current) || []
    let next = null

    for (const n of neigh) {
      const ek = edgeKey(current, n)
      if (visitedEdge.has(ek)) continue
      if (prev != null && n === prev && neigh.length > 1) continue
      next = n
      visitedEdge.add(ek)
      break
    }

    if (next == null) break
    prev = current
    current = next

    if (current === start) break
    if (path.length > 20000) break
  }

  if (path.length > 2 && path[0].distanceToSquared(path[path.length - 1]) < 1e-10) {
    path.pop()
  }

  return path
}

function loopToSegments(loop) {
  if (!loop || loop.length < 2) return []
  const segs = []
  for (let i = 0; i < loop.length; i++) {
    const a = loop[i]
    const b = loop[(i + 1) % loop.length]
    if (a.distanceToSquared(b) < 1e-10) continue
    segs.push({ a: a.clone(), b: b.clone() })
  }
  return segs
}

// ======================================================
// ✅ CLIP helper (WORLD XZ)
// ======================================================
function isInsideClipXZ(pWorld, clipBoxWorld, marginXZ = 0) {
  if (!clipBoxWorld) return true
  return (
    pWorld.x >= clipBoxWorld.min.x - marginXZ &&
    pWorld.x <= clipBoxWorld.max.x + marginXZ &&
    pWorld.z >= clipBoxWorld.min.z - marginXZ &&
    pWorld.z <= clipBoxWorld.max.z + marginXZ
  )
}

// ======================================================
// ✅ OUTER-DIR filter (robust dla stromych dachów)
// - porównujemy tylko kierunek w XZ, a gdy outerDir jest prawie pionowy -> ignorujemy filtr
// ======================================================
function passesOuterDirXZ(worldNormal, outerDirWorld, outMinDot = ROOF_OUT_MIN_DOT) {
  if (!outerDirWorld) return true

  const od = outerDirWorld.clone()
  od.y = 0
  if (od.lengthSq() < 1e-10) return true // outerDir prawie pionowy -> nie filtrujemy
  od.normalize()

  const nn = worldNormal.clone()
  nn.y = 0
  if (nn.lengthSq() < 1e-10) return true // normal prawie pionowy -> nie filtrujemy
  nn.normalize()

  return nn.dot(od) >= outMinDot
}

// ======================================================
// ✅ Obrys dachu (okap) - TOP SURFACE + OUTER(XZ) + CLIP
// ======================================================
function getRoofPerimeterHullSegmentsWorld_TOP(
  roofMesh,
  outerDirWorld,
  clipBoxWorld,
  marginXZ = ROOF_CLIP_MARGIN_XZ,
  epsXZ = 1e-4,
  minNormalY = 0.05,
  yTol = Y_TOP_TOL,
  outMinDot = ROOF_OUT_MIN_DOT
) {
  if (!roofMesh?.geometry?.attributes?.position) return []

  const geom = roofMesh.geometry
  const pos = geom.attributes.position
  const index = geom.index
  const mw = roofMesh.matrixWorld

  const sampleYMax = makeRoofYMaxSampler(roofMesh, Y_SAMPLE_EPS_XZ)

  const uniq = new Map()
  const keyXZ = (x, z) => `${Math.round(x / epsXZ)},${Math.round(z / epsXZ)}`

  const a = new THREE.Vector3()
  const b = new THREE.Vector3()
  const c = new THREE.Vector3()
  const ab = new THREE.Vector3()
  const ac = new THREE.Vector3()
  const n = new THREE.Vector3()
  const centroid = new THREE.Vector3()

  const addXZ = (v) => {
    const k = keyXZ(v.x, v.z)
    if (!uniq.has(k)) uniq.set(k, { x: v.x, z: v.z })
  }

  const takeTri = (ia, ib, ic) => {
    a.set(pos.getX(ia), pos.getY(ia), pos.getZ(ia)).applyMatrix4(mw)
    b.set(pos.getX(ib), pos.getY(ib), pos.getZ(ib)).applyMatrix4(mw)
    c.set(pos.getX(ic), pos.getY(ic), pos.getZ(ic)).applyMatrix4(mw)

    ab.subVectors(b, a)
    ac.subVectors(c, a)
    n.crossVectors(ab, ac)

    const len = n.length()
    if (len < 1e-12) return
    n.multiplyScalar(1 / len) // normal w WORLD

    // top surface
    if (n.y < minNormalY) return

    // outer filter tylko w XZ (dla stromych dachów nie wytnie całej połaci)
    if (!passesOuterDirXZ(n, outerDirWorld, outMinDot)) return

    centroid.copy(a).add(b).add(c).multiplyScalar(1 / 3)
    if (!isInsideClipXZ(centroid, clipBoxWorld, marginXZ)) return

    const yMax = sampleYMax(centroid.x, centroid.z)
    if (centroid.y < yMax - yTol) return

    addXZ(a)
    addXZ(b)
    addXZ(c)
  }

  if (index) {
    for (let i = 0; i < index.count; i += 3) {
      takeTri(index.getX(i), index.getX(i + 1), index.getX(i + 2))
    }
  } else {
    for (let i = 0; i < pos.count; i += 3) {
      takeTri(i, i + 1, i + 2)
    }
  }

  const pts = Array.from(uniq.values())
  if (pts.length < 3) return []

  pts.sort((p1, p2) => (p1.x === p2.x ? p1.z - p2.z : p1.x - p2.x))
  const cross2 = (o, a2, b2) => (a2.x - o.x) * (b2.z - o.z) - (a2.z - o.z) * (b2.x - o.x)

  const lower = []
  for (const p of pts) {
    while (lower.length >= 2 && cross2(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop()
    lower.push(p)
  }

  const upper = []
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i]
    while (upper.length >= 2 && cross2(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop()
    upper.push(p)
  }

  const hull = lower.slice(0, -1).concat(upper.slice(0, -1))
  if (hull.length < 3) return []

  const loop = hull.map((p) => new THREE.Vector3(p.x, sampleYMax(p.x, p.z), p.z))
  return loopToSegments(loop)
}

// ======================================================
// ✅ Połacie dachu (2 face) - TOP SURFACE + OUTER(XZ) + CLIP
// ======================================================
function extractRoofFacesFromMeshWorld_TOP(roofMesh, options = {}) {
  if (!roofMesh?.geometry?.attributes?.position) return []

  const eps = options.eps ?? 1e-4
  const minAreaAbs = options.minAreaAbs ?? 1e-8
  const minNormalYAbs = options.minNormalYAbs ?? 0.05
  const ridgeBandRatio = options.ridgeBandRatio ?? 0.12
  const yTol = options.yTol ?? Y_TOP_TOL

  const outerDirWorld = options.outerDirWorld || null
  const outMinDot = options.outMinDot ?? ROOF_OUT_MIN_DOT

  const clipBoxWorld = options.clipBoxWorld || null
  const marginXZ = options.marginXZ ?? ROOF_CLIP_MARGIN_XZ

  const geom = roofMesh.geometry
  const pos = geom.attributes.position
  const index = geom.index
  const mw = roofMesh.matrixWorld.clone()

  const sampleYMax = makeRoofYMaxSampler(roofMesh, Y_SAMPLE_EPS_XZ)

  const tmpA = new THREE.Vector3()
  const tmpB = new THREE.Vector3()
  const tmpC = new THREE.Vector3()
  const centroid = new THREE.Vector3()

  const qKey = (v) => `${Math.round(v.x / eps)},${Math.round(v.y / eps)},${Math.round(v.z / eps)}`

  const tris = []
  const allPts = []

  const makeTri = (ia, ib, ic) => {
    const a = tmpA.set(pos.getX(ia), pos.getY(ia), pos.getZ(ia)).clone().applyMatrix4(mw)
    const b = tmpB.set(pos.getX(ib), pos.getY(ib), pos.getZ(ib)).clone().applyMatrix4(mw)
    const c = tmpC.set(pos.getX(ic), pos.getY(ic), pos.getZ(ic)).clone().applyMatrix4(mw)

    const ab = new THREE.Vector3().subVectors(b, a)
    const ac = new THREE.Vector3().subVectors(c, a)
    const n = new THREE.Vector3().crossVectors(ab, ac) // normal w WORLD (bo a/b/c są w WORLD)
    const area2 = n.length()
    if (area2 <= minAreaAbs) return null
    n.normalize()

    if (n.y < minNormalYAbs) return null

    // outer filter tylko w XZ (a gdy outerDir pionowy, filtr się wyłącza)
    if (!passesOuterDirXZ(n, outerDirWorld, outMinDot)) return null

    centroid.copy(a).add(b).add(c).multiplyScalar(1 / 3)
    if (!isInsideClipXZ(centroid, clipBoxWorld, marginXZ)) return null

    const yMax = sampleYMax(centroid.x, centroid.z)
    if (centroid.y < yMax - yTol) return null

    return {
      a: a.clone(),
      b: b.clone(),
      c: c.clone(),
      centroid: centroid.clone(),
      normal: n.clone(),
      area: area2 * 0.5,
    }
  }

  if (index) {
    for (let i = 0; i < index.count; i += 3) {
      const tri = makeTri(index.getX(i), index.getX(i + 1), index.getX(i + 2))
      if (tri) {
        tris.push(tri)
        allPts.push(tri.a, tri.b, tri.c)
      }
    }
  } else {
    for (let i = 0; i < pos.count; i += 3) {
      const tri = makeTri(i, i + 1, i + 2)
      if (tri) {
        tris.push(tri)
        allPts.push(tri.a, tri.b, tri.c)
      }
    }
  }

  if (!tris.length) return []

  const roofBox = new THREE.Box3().setFromPoints(allPts)
  const center = roofBox.getCenter(new THREE.Vector3())
  const ySpan = Math.max(1e-6, roofBox.max.y - roofBox.min.y)
  const ridgeBand = Math.max(0.01, ySpan * ridgeBandRatio)

  const topCandidates = allPts.filter((p) => p.y >= roofBox.max.y - ridgeBand)

  const topMap = new Map()
  for (const p of topCandidates) {
    const k = qKey(p)
    if (!topMap.has(k)) topMap.set(k, p.clone())
  }
  const topPts = Array.from(topMap.values())

  let ridgeDir = new THREE.Vector3(1, 0, 0)
  if (topPts.length >= 2) {
    let bestI = 0
    let bestJ = 1
    let bestD2 = -1
    for (let i = 0; i < topPts.length; i++) {
      for (let j = i + 1; j < topPts.length; j++) {
        const dx = topPts[j].x - topPts[i].x
        const dz = topPts[j].z - topPts[i].z
        const d2 = dx * dx + dz * dz
        if (d2 > bestD2) {
          bestD2 = d2
          bestI = i
          bestJ = j
        }
      }
    }
    ridgeDir = new THREE.Vector3(topPts[bestJ].x - topPts[bestI].x, 0, topPts[bestJ].z - topPts[bestI].z)
    if (ridgeDir.lengthSq() < 1e-10) ridgeDir.set(1, 0, 0)
    else ridgeDir.normalize()
  } else {
    const sx = roofBox.max.x - roofBox.min.x
    const sz = roofBox.max.z - roofBox.min.z
    ridgeDir = sx >= sz ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 0, 1)
  }

  let splitDir = new THREE.Vector3(-ridgeDir.z, 0, ridgeDir.x)
  if (splitDir.lengthSq() < 1e-10) splitDir = new THREE.Vector3(0, 0, 1)
  splitDir.normalize()

  const groups = [
    { tris: [], area: 0 },
    { tris: [], area: 0 },
  ]

  for (const tri of tris) {
    const rel = tri.centroid.clone().sub(center)
    const s = rel.dot(splitDir)
    const idx = Math.abs(s) < 1e-6 ? (groups[0].area <= groups[1].area ? 0 : 1) : s < 0 ? 0 : 1
    groups[idx].tris.push(tri)
    groups[idx].area += tri.area
  }

  const buildFace = (group, faceIdx) => {
    if (!group.tris.length) return null

    const edgeMap = new Map()
    const qKeyEdge = (v) => `${Math.round(v.x / eps)},${Math.round(v.y / eps)},${Math.round(v.z / eps)}`

    const addEdge = (pA, pB) => {
      const ka = qKeyEdge(pA)
      const kb = qKeyEdge(pB)
      const key = ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`
      if (!edgeMap.has(key)) edgeMap.set(key, { count: 1, a: pA.clone(), b: pB.clone() })
      else edgeMap.get(key).count += 1
    }

    for (const t of group.tris) {
      addEdge(t.a, t.b)
      addEdge(t.b, t.c)
      addEdge(t.c, t.a)
    }

    const boundarySegments = []
    for (const e of edgeMap.values()) {
      if (e.count === 1) boundarySegments.push({ a: e.a.clone(), b: e.b.clone() })
    }

    const loop = buildLoopFromSegments(boundarySegments, 1e-3)

    const loopSnapped = loop
      .map((p) => new THREE.Vector3(p.x, sampleYMax(p.x, p.z), p.z))
      .filter((p) => isInsideClipXZ(p, clipBoxWorld, marginXZ))

    if (loopSnapped.length < 3) return null

    return {
      key: `roof-face-${faceIdx}`,
      points: loopSnapped,
      segments: loopToSegments(loopSnapped),
      area: group.area,
    }
  }

  return groups
    .map((g, i) => buildFace(g, i))
    .filter(Boolean)
    .sort((a, b) => (b.area ?? 0) - (a.area ?? 0))
}

// ======================================================
// COMPONENT
// ======================================================
export default function GarazDwuspadowy({ gatesData, doorsData, windowsData, textures, onTrimsData, ...props }) {
  const { nodes, materials } = useGLTF('/models/garages/dwuspadowy.glb')

  const scaledRightGeometry = useMemo(() => {
    const g = cloneAndScaleUV(
      nodes.Right_side.geometry,
      props.length / 100,
      props.height / 100,
      props.width / 100,
      props.length / 100,
      props.height / 100
    )
    return ensureBoundingBox(g)
  }, [nodes.Right_side.geometry, props.length, props.height, props.width])

  const scaledLeftGeometry = useMemo(() => {
    const g = cloneAndScaleUV(
      nodes.Left_side.geometry,
      props.length / 100,
      props.height / 100,
      props.width / 100,
      props.length / 100,
      props.height / 100
    )
    return ensureBoundingBox(g)
  }, [nodes.Left_side.geometry, props.length, props.height, props.width])

  const scaledBackGeometry = useMemo(() => {
    const g = cloneAndScaleUV(
      nodes.Back_side.geometry,
      props.length / 100,
      props.height / 100,
      props.width / 100,
      props.width / 100,
      props.height / 100
    )
    return ensureBoundingBox(g)
  }, [nodes.Back_side.geometry, props.length, props.height, props.width])

  const scaledFrontGeometry = useMemo(() => {
    const g = cloneAndScaleUV(
      nodes.Front_side.geometry,
      props.length / 100,
      props.height / 100,
      props.width / 100,
      props.width / 100,
      props.height / 100
    )
    return ensureBoundingBox(g)
  }, [nodes.Front_side.geometry, props.length, props.height, props.width])

  const scaledRoofGeometry = useMemo(() => {
    const g = cloneAndScaleUV(
      nodes.Celing.geometry,
      props.length / 100,
      props.height / 100,
      props.width / 100,
      props.length / 100,
      props.width / 100
    )
    return ensureBoundingBox(g)
  }, [nodes.Celing.geometry, props.length, props.height, props.width])

  const garageCenter = useMemo(() => {
    const box = unionBoxes([scaledLeftGeometry, scaledRightGeometry, scaledFrontGeometry, scaledBackGeometry])
    const c = new THREE.Vector3()
    box.getCenter(c)
    return c
  }, [scaledLeftGeometry, scaledRightGeometry, scaledFrontGeometry, scaledBackGeometry])

  const dirLeft = useMemo(() => getCenterFromGeo(scaledLeftGeometry).sub(garageCenter).normalize(), [scaledLeftGeometry, garageCenter])
  const dirRight = useMemo(() => getCenterFromGeo(scaledRightGeometry).sub(garageCenter).normalize(), [scaledRightGeometry, garageCenter])
  const dirFront = useMemo(() => getCenterFromGeo(scaledFrontGeometry).sub(garageCenter).normalize(), [scaledFrontGeometry, garageCenter])
  const dirBack = useMemo(() => getCenterFromGeo(scaledBackGeometry).sub(garageCenter).normalize(), [scaledBackGeometry, garageCenter])

  const [flattenedWindows, setFlattenedWindows] = useState({})
  const flattenWindows = (data) => {
    const result = {}
    if (!data) return result
    Object.entries(data).forEach(([wallKey, windowTypes]) => {
      const wallWindows = []
      Object.entries(windowTypes || {}).forEach(([typeKey, windows]) => {
        if (!Array.isArray(windows)) return
        windows.forEach((w) => wallWindows.push({ ...w, wall: wallKey, type: typeKey }))
      })
      result[wallKey] = wallWindows
    })
    return result
  }

  useEffect(() => {
    setFlattenedWindows(flattenWindows(windowsData))
  }, [windowsData])

  // ===== subtractions =====
  const subtractionGeometriesFrontWall = useMemo(() => {
    const tmp = []
    if (gatesData?.length) {
      gatesData.forEach((wallData) => {
        if (!wallData.closedBoundingBox) return
        const { size } = wallData.closedBoundingBox
        tmp.push({
          geometry: new THREE.BoxGeometry(size.x, size.y, size.z),
          position: wallData.positionAdjusted || [0, 0, 0],
        })
      })
    }
    if (doorsData?.front_wall?.length) {
      doorsData.front_wall.forEach((wallData) => {
        if (!wallData.closedBoundingBox) return
        const { size } = wallData.closedBoundingBox
        tmp.push({
          geometry: new THREE.BoxGeometry(size.x, size.y, size.z),
          position: wallData.positionAdjusted || [0, 0, 0],
          scale: wallData.scale,
        })
      })
    }
    return tmp
  }, [gatesData, doorsData?.front_wall])

  const subtractionGeometriesBackWall = useMemo(() => {
    const tmp = []
    if (doorsData?.back_wall?.length) {
      doorsData.back_wall.forEach((wallData) => {
        if (!wallData.closedBoundingBox) return
        const { size } = wallData.closedBoundingBox
        tmp.push({
          geometry: new THREE.BoxGeometry(size.x, size.y, size.z),
          position: wallData.positionAdjusted || [0, 0, 0],
          scale: wallData.scale,
        })
      })
    }
    if (flattenedWindows?.back_wall?.length) {
      flattenedWindows.back_wall.forEach((wallData) => {
        if (!wallData.closedBoundingBox) return
        const { size } = wallData.closedBoundingBox
        tmp.push({
          geometry: new THREE.BoxGeometry(size.x, size.y, size.z),
          position: wallData.positionAdjusted || [0, 0, 0],
          scale: wallData.scale,
        })
      })
    }
    return tmp
  }, [doorsData?.back_wall, flattenedWindows?.back_wall])

  const subtractionGeometriesLeftWall = useMemo(() => {
    const tmp = []
    if (doorsData?.left_wall?.length) {
      doorsData.left_wall.forEach((wallData) => {
        if (!wallData.closedBoundingBox) return
        const { size } = wallData.closedBoundingBox
        tmp.push({
          geometry: new THREE.BoxGeometry(size.x, size.y, size.z),
          position: wallData.positionAdjusted || [0, 0, 0],
          scale: wallData.scale,
        })
      })
    }
    if (flattenedWindows?.left_wall?.length) {
      flattenedWindows.left_wall.forEach((wallData) => {
        if (!wallData.closedBoundingBox) return
        const { size } = wallData.closedBoundingBox
        tmp.push({
          geometry: new THREE.BoxGeometry(size.x, size.y, size.z),
          position: wallData.positionAdjusted || [0, 0, 0],
          scale: wallData.scale,
        })
      })
    }
    return tmp
  }, [doorsData?.left_wall, flattenedWindows?.left_wall])

  const subtractionGeometriesRightWall = useMemo(() => {
    const tmp = []
    if (doorsData?.right_wall?.length) {
      doorsData.right_wall.forEach((wallData) => {
        if (!wallData.closedBoundingBox) return
        const { size } = wallData.closedBoundingBox
        tmp.push({
          geometry: new THREE.BoxGeometry(size.x, size.y, size.z),
          position: wallData.positionAdjusted || [0, 0, 0],
          scale: wallData.scale,
        })
      })
    }
    if (flattenedWindows?.right_wall?.length) {
      flattenedWindows.right_wall.forEach((wallData) => {
        if (!wallData.closedBoundingBox) return
        const { size } = wallData.closedBoundingBox
        tmp.push({
          geometry: new THREE.BoxGeometry(size.x, size.y, size.z),
          position: wallData.positionAdjusted || [0, 0, 0],
          scale: wallData.scale,
        })
      })
    }
    return tmp
  }, [doorsData?.right_wall, flattenedWindows?.right_wall])

  // ===== materials helpers =====
  const applyRepeat = (tex, rx, ry) => {
    if (!tex) return
    setupTextureRepeat(tex, rx, ry)
  }

  const baseTemplate = useMemo(() => {
    return materials?.Material || materials?.['Material'] || new THREE.MeshStandardMaterial()
  }, [materials])

  const cloneBase = () => baseTemplate.clone()
  const EPS = 0.02

  const makeOuterWallMaterial = (repeatX, repeatY, dir) => {
    const m = cloneBase()
    const wallColor = textures?.walls_color
    m.side = THREE.DoubleSide
    m.transparent = false

    if (wallColor) {
      m.map = wallColor.textureDiff || null
      m.metalnessMap = wallColor.textureMetalness || null
      m.normalMap = wallColor.textureNormalness || null

      applyRepeat(m.map, repeatX / wallColor.multiplierHorizontal, repeatY / wallColor.multiplierVertical)
      applyRepeat(m.metalnessMap, repeatX / wallColor.multiplierHorizontal, repeatY / wallColor.multiplierVertical)
      applyRepeat(m.normalMap, repeatX / wallColor.multiplierHorizontal, repeatY / wallColor.multiplierVertical)

      m.metalness = wallColor.metalnessValue ?? 0
      m.roughness = wallColor.roughnessValue ?? 1
      m.color = new THREE.Color(wallColor.hex ?? '#ffffff')
    } else {
      m.color = new THREE.Color('#ffffff')
      m.metalness = 0
      m.roughness = 1
    }

    applyNormalDirectionFilter(m, dir, 'out', EPS)
    m.needsUpdate = true
    return m
  }

  const makeInnerWallMaterial = (repeatX, repeatY, dir) => {
    const m = cloneBase()
    const wallColor = textures?.walls_inside_color
    m.side = THREE.DoubleSide
    m.transparent = false

    if (wallColor) {
      m.map = wallColor.textureDiff || null
      m.metalnessMap = wallColor.textureMetalness || null
      m.normalMap = wallColor.textureNormalness || null

      applyRepeat(m.map, repeatX / wallColor.multiplierHorizontal, repeatY / wallColor.multiplierVertical)
      applyRepeat(m.metalnessMap, repeatX / wallColor.multiplierHorizontal, repeatY / wallColor.multiplierVertical)
      applyRepeat(m.normalMap, repeatX / wallColor.multiplierHorizontal, repeatY / wallColor.multiplierVertical)

      m.metalness = wallColor.metalnessValue ?? 0
      m.roughness = wallColor.roughnessValue ?? 1
      m.color = new THREE.Color(wallColor.hex ?? '#f5f5f5')
    } else {
      m.color = new THREE.Color('#f5f5f5')
      m.metalness = 0
      m.roughness = 1
    }

    applyNormalDirectionFilter(m, dir, 'in', EPS)
    m.needsUpdate = true
    return m
  }

  const leftOuterMat = useMemo(() => makeOuterWallMaterial(props.length / 100, 1, dirLeft), [
    baseTemplate,
    textures?.walls_color,
    props.length,
    dirLeft,
  ])
  const leftInnerMat = useMemo(() => makeInnerWallMaterial(props.length / 100, 1, dirLeft), [
    baseTemplate,
    textures?.walls_inside_color,
    props.length,
    dirLeft,
  ])

  const rightOuterMat = useMemo(() => makeOuterWallMaterial(props.length / 100, 1, dirRight), [
    baseTemplate,
    textures?.walls_color,
    props.length,
    dirRight,
  ])
  const rightInnerMat = useMemo(() => makeInnerWallMaterial(props.length / 100, 1, dirRight), [
    baseTemplate,
    textures?.walls_inside_color,
    props.length,
    dirRight,
  ])

  const frontOuterMat = useMemo(() => makeOuterWallMaterial(1, 1, dirFront), [
    baseTemplate,
    textures?.walls_color,
    dirFront,
  ])
  const frontInnerMat = useMemo(() => makeInnerWallMaterial(1, 1, dirFront), [
    baseTemplate,
    textures?.walls_inside_color,
    dirFront,
  ])

  const backOuterMat = useMemo(() => makeOuterWallMaterial(1, 1, dirBack), [
    baseTemplate,
    textures?.walls_color,
    dirBack,
  ])
  const backInnerMat = useMemo(() => makeInnerWallMaterial(1, 1, dirBack), [
    baseTemplate,
    textures?.walls_inside_color,
    dirBack,
  ])

  // ==========================================================
  // ROOF materials (nie ruszam renderu)
  // ==========================================================
  const roofOuterDir = useMemo(() => {
    const nAttr = scaledRoofGeometry?.attributes?.normal
    if (!nAttr || !nAttr.array) return new THREE.Vector3(0, 1, 0)
    const arr = nAttr.array
    let sumY = 0
    for (let i = 1; i < arr.length; i += 3) sumY += arr[i]
    const sign = sumY >= 0 ? 1 : -1
    return new THREE.Vector3(0, sign, 0)
  }, [scaledRoofGeometry])

  const RoofOuterMaterial = useMemo(() => {
    const m = cloneBase()
    const RoofColor = textures?.roof_color || null
    m.side = THREE.DoubleSide
    m.transparent = false

    if (RoofColor) {
      m.map = RoofColor.textureDiff || null
      m.metalnessMap = RoofColor.textureMetalness || null
      m.normalMap = RoofColor.textureNormalness || null

      applyRepeat(m.map, 1 / (RoofColor.multiplierHorizontal || 1), 1 / (RoofColor.multiplierVertical || 1))
      applyRepeat(m.metalnessMap, 1 / (RoofColor.multiplierHorizontal || 1), 1 / (RoofColor.multiplierVertical || 1))
      applyRepeat(m.normalMap, 1 / (RoofColor.multiplierHorizontal || 1), 1 / (RoofColor.multiplierVertical || 1))

      m.metalness = RoofColor.metalnessValue ?? 0
      m.roughness = RoofColor.roughnessValue ?? RoofColor.rougnessValue ?? 1
      m.color = new THREE.Color(RoofColor.hex ?? '#ffffff')
    } else {
      m.color = new THREE.Color('#ffffff')
      m.metalness = 0
      m.roughness = 1
    }

    applyNormalDirectionFilter(m, roofOuterDir, 'out', EPS)
    m.needsUpdate = true
    return m
  }, [baseTemplate, textures?.roof_color, roofOuterDir])

  const RoofInnerMaterial = useMemo(() => {
    const m = cloneBase()
    const RoofColor = textures?.roof_color || null
    m.side = THREE.DoubleSide
    m.transparent = false

    if (RoofColor) {
      m.map = RoofColor.textureDiff || null
      m.metalnessMap = RoofColor.textureMetalness || null
      m.normalMap = RoofColor.textureNormalness || null

      applyRepeat(m.map, 1 / (RoofColor.multiplierHorizontal || 1), 1 / (RoofColor.multiplierVertical || 1))
      applyRepeat(m.metalnessMap, 1 / (RoofColor.multiplierHorizontal || 1), 1 / (RoofColor.multiplierVertical || 1))
      applyRepeat(m.normalMap, 1 / (RoofColor.multiplierHorizontal || 1), 1 / (RoofColor.multiplierVertical || 1))

      m.metalness = RoofColor.metalnessValue ?? 0
      m.roughness = RoofColor.roughnessValue ?? RoofColor.rougnessValue ?? 1
      m.color = new THREE.Color(RoofColor.hex ?? '#ffffff')
    } else {
      m.color = new THREE.Color('#ffffff')
      m.metalness = 0
      m.roughness = 1
    }

    applyNormalDirectionFilter(m, roofOuterDir, 'in', EPS)
    m.needsUpdate = true
    return m
  }, [baseTemplate, textures?.roof_color, roofOuterDir])

  const RoofInsideMaterial = RoofInnerMaterial

  // ==========================================================
  // ✅ TOOLING
  // ==========================================================
  const groupRef = useRef(null)
  const roofOuterRef = useRef(null)

  // ✅ refs do ścian (żeby clipBox był zawsze zgodny z realnym renderem)
  const frontWallRef = useRef(null)
  const backWallRef = useRef(null)
  const leftWallRef = useRef(null)
  const rightWallRef = useRef(null)

  useLayoutEffect(() => {
    if (!onTrimsData) return
    if (!groupRef.current || !roofOuterRef.current) return

    groupRef.current.updateWorldMatrix(true, true)
    roofOuterRef.current.updateWorldMatrix(true, true)

    // ✅ wallsBoxWorld liczony z REALNYCH meshów ścian
    const wallsBoxWorld = new THREE.Box3().makeEmpty()
    const wallMeshes = [frontWallRef.current, backWallRef.current, leftWallRef.current, rightWallRef.current].filter(Boolean)

    if (wallMeshes.length) {
      for (const m of wallMeshes) {
        m.updateWorldMatrix(true, true)
        wallsBoxWorld.union(new THREE.Box3().setFromObject(m))
      }
    } else {
      // fallback (gdyby refs nie było)
      const wallsBoxLocal = unionBoxes([scaledLeftGeometry, scaledRightGeometry, scaledFrontGeometry, scaledBackGeometry])
      wallsBoxWorld.copy(wallsBoxLocal).applyMatrix4(groupRef.current.matrixWorld)
    }

    const groundY = wallsBoxWorld.min.y

    // OuterDir zostawiamy (materiały go potrzebują), ale selekcja geometrii dachu jest teraz odporna:
    // filtr outerDir działa tylko w XZ i automatycznie się wyłącza gdy outerDir jest pionowy.
    const outerDirWorld = roofOuterDir.clone().transformDirection(roofOuterRef.current.matrixWorld).normalize()

    const roofEdges = getRoofPerimeterHullSegmentsWorld_TOP(
      roofOuterRef.current,
      outerDirWorld,
      wallsBoxWorld,
      ROOF_CLIP_MARGIN_XZ,
      1e-4,
      0.05,
      Y_TOP_TOL,
      ROOF_OUT_MIN_DOT
    )

    const roofFaces = extractRoofFacesFromMeshWorld_TOP(roofOuterRef.current, {
      eps: 1e-4,
      minNormalYAbs: 0.05,
      ridgeBandRatio: 0.12,
      yTol: Y_TOP_TOL,
      outerDirWorld,
      outMinDot: ROOF_OUT_MIN_DOT,
      clipBoxWorld: wallsBoxWorld,
      marginXZ: ROOF_CLIP_MARGIN_XZ,
    })

    // narożniki: bierzemy yMax w WORLD (na podstawie roofOuterRef)
    const sampleYMax = makeRoofYMaxSampler(roofOuterRef.current, Y_SAMPLE_EPS_XZ)

    const minX = wallsBoxWorld.min.x
    const maxX = wallsBoxWorld.max.x
    const minZ = wallsBoxWorld.min.z
    const maxZ = wallsBoxWorld.max.z

    const makeCornerTop = (x, z) => new THREE.Vector3(x, sampleYMax(x, z), z)

    const roofCorners = {
      frontLeft: makeCornerTop(minX, minZ),
      frontRight: makeCornerTop(minX, maxZ),
      backLeft: makeCornerTop(maxX, minZ),
      backRight: makeCornerTop(maxX, maxZ),
    }

    const cornerSegments = Object.entries(roofCorners).map(([key, top]) => ({
      key,
      a: new THREE.Vector3(top.x, groundY, top.z),
      b: top.clone(),
    }))

    onTrimsData({
      roofEdges,
      roofFaces,
      corners: cornerSegments,
      roofCorners,
      groundY,
    })
  }, [
    onTrimsData,
    props.length,
    props.width,
    props.height,
    scaledLeftGeometry,
    scaledRightGeometry,
    scaledFrontGeometry,
    scaledBackGeometry,
    scaledRoofGeometry,
    gatesData,
    doorsData,
    windowsData,
    roofOuterDir,
  ])

  return (
    <group ref={groupRef} {...props} dispose={null} castShadow receiveShadow>
      {/* ROOF outer (ref do toolingu) */}
      <mesh
        ref={roofOuterRef}
        name="Roof_outer"
        geometry={scaledRoofGeometry}
        material={RoofOuterMaterial}
        position={nodes.Celing.position}
        rotation={nodes.Celing.rotation}
      />

      {/* ROOF inner (render only) */}
      <mesh
        name="Roof_inner"
        geometry={scaledRoofGeometry}
        material={RoofInsideMaterial}
        position={nodes.Celing.position}
        rotation={nodes.Celing.rotation}
      />

      {/* FRONT OUTER */}
      <mesh ref={frontWallRef} name="Front_side_outer" material={frontOuterMat} position={nodes.Front_side.position} rotation={nodes.Front_side.rotation}>
        <Geometry>
          <Base geometry={scaledFrontGeometry} />
          {subtractionGeometriesFrontWall.map(({ geometry, position, scale }, index) => (
            <Subtraction key={index} geometry={geometry} position={position} scale={scale} />
          ))}
        </Geometry>
      </mesh>

      {/* FRONT INNER */}
      <mesh name="Front_side_inner" material={frontInnerMat} position={nodes.Front_side.position} rotation={nodes.Front_side.rotation}>
        <Geometry>
          <Base geometry={scaledFrontGeometry} />
          {subtractionGeometriesFrontWall.map(({ geometry, position, scale }, index) => (
            <Subtraction key={index} geometry={geometry} position={position} scale={scale} />
          ))}
        </Geometry>
      </mesh>

      {/* LEFT OUTER */}
      <mesh ref={leftWallRef} name="Left_side_outer" material={leftOuterMat} position={nodes.Left_side.position} rotation={nodes.Left_side.rotation}>
        <Geometry>
          <Base geometry={scaledLeftGeometry} />
          {subtractionGeometriesLeftWall.map(({ geometry, position, scale }, index) => (
            <Subtraction key={index} geometry={geometry} position={position} scale={scale} />
          ))}
        </Geometry>
      </mesh>

      {/* LEFT INNER */}
      <mesh name="Left_side_inner" material={leftInnerMat} position={nodes.Left_side.position} rotation={nodes.Left_side.rotation}>
        <Geometry>
          <Base geometry={scaledLeftGeometry} />
          {subtractionGeometriesLeftWall.map(({ geometry, position, scale }, index) => (
            <Subtraction key={index} geometry={geometry} position={position} scale={scale} />
          ))}
        </Geometry>
      </mesh>

      {/* RIGHT OUTER */}
      <mesh ref={rightWallRef} name="Right_side_outer" material={rightOuterMat} position={nodes.Right_side.position} rotation={nodes.Right_side.rotation}>
        <Geometry>
          <Base geometry={scaledRightGeometry} />
          {subtractionGeometriesRightWall.map(({ geometry, position, scale }, index) => (
            <Subtraction key={index} geometry={geometry} position={position} scale={scale} />
          ))}
        </Geometry>
      </mesh>

      {/* RIGHT INNER */}
      <mesh name="Right_side_inner" material={rightInnerMat} position={nodes.Right_side.position} rotation={nodes.Right_side.rotation}>
        <Geometry>
          <Base geometry={scaledRightGeometry} />
          {subtractionGeometriesRightWall.map(({ geometry, position, scale }, index) => (
            <Subtraction key={index} geometry={geometry} position={position} scale={scale} />
          ))}
        </Geometry>
      </mesh>

      {/* BACK OUTER */}
      <mesh ref={backWallRef} name="Back_side_outer" material={backOuterMat} position={nodes.Back_side.position} rotation={nodes.Back_side.rotation}>
        <Geometry>
          <Base geometry={scaledBackGeometry} />
          {subtractionGeometriesBackWall.map(({ geometry, position, scale }, index) => (
            <Subtraction key={index} geometry={geometry} position={position} scale={scale} />
          ))}
        </Geometry>
      </mesh>

      {/* BACK INNER */}
      <mesh name="Back_side_inner" material={backInnerMat} position={nodes.Back_side.position} rotation={nodes.Back_side.rotation}>
        <Geometry>
          <Base geometry={scaledBackGeometry} />
          {subtractionGeometriesBackWall.map(({ geometry, position, scale }, index) => (
            <Subtraction key={index} geometry={geometry} position={position} scale={scale} />
          ))}
        </Geometry>
      </mesh>
    </group>
  )
}

useGLTF.preload('/models/garages/dwuspadowy.glb')
